import { useEffect, useState } from "react";
import { AuditTable } from "../../components/AuditTable";
import { Shell } from "../../components/Shell";
import { Button, Confirm, Empty, EnvBadge, ErrorNotice, Field, Input, Loading, Money, PageHead, Pager, Panel, Segmented, GameStatusBadge, StatusBadge, useToast } from "../../components/ui";
import { adminApi } from "../../lib/api";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { Link, navigate, useSearchParam } from "../../lib/router";
import { useClient } from "../../lib/session";
import { formatDateTime, formatGameTime, relativeTime } from "../../lib/time";
import { POWER_SCOPES, type ApiKey } from "../../lib/types";

export function AdminGames() {
  const api = adminApi(useClient());
  const [q, setQ] = useSearchParam("q");
  const [status, setStatus] = useSearchParam("status");
  const [search, setSearch] = useState(q);
  const [offset, setOffset] = useState(0);
  const limit = 25;
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (search !== q) {
        setOffset(0);
        setQ(search);
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [search]);
  const list = useQuery(`admin:games:${q}:${status}:${offset}`, () => api.games({ q, status, limit, offset }));

  return (
    <Shell crumbs={[{ label: "Games" }]}>
      <PageHead title="Games" lede="Every game on the engine, across all developers." />
      <Panel
        flush
        title={list.data ? `${list.data.total} game${list.data.total === 1 ? "" : "s"}` : "Games"}
        actions={
          <div className="row">
            <Input type="search" placeholder="Search game or developer" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search games" style={{ width: 240 }} />
            <Segmented
              label="Filter by status"
              value={status || "all"}
              onChange={(v) => {
                setOffset(0);
                setStatus(v === "all" ? "" : v);
              }}
              options={[{ value: "all", label: "All" }, { value: "active", label: "Active" }, { value: "suspended", label: "Suspended" }, { value: "closed", label: "Deleted" }]}
            />
          </div>
        }
      >
        <ErrorNotice error={list.error} onRetry={list.reload} />
        {list.loading && !list.data && <Loading />}
        {list.data && list.data.data.length === 0 && <Empty title="No games match">Developers create games after you register them.</Empty>}
        {list.data && list.data.data.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Developer</th>
                    <th>Status</th>
                    <th className="num">Banks</th>
                    <th className="num">Accounts (sandbox / live)</th>
                    <th className="num">Keys (sandbox / live)</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.data.map((g) => (
                    <tr key={g.id} className="clickable" onClick={() => navigate(`/admin/games/${g.id}`)}>
                      <td>
                        <Link to={`/admin/games/${g.id}`} className="cell-title" onClick={(e) => e.stopPropagation()}>{g.name}</Link>
                        <div className="cell-sub mono">{g.slug}</div>
                      </td>
                      <td>
                        <Link to={`/admin/developers/${g.developer_id}`} onClick={(e) => e.stopPropagation()}>{g.developer_name}</Link>
                      </td>
                      <td><GameStatusBadge status={g.status} /></td>
                      <td className="num">{g.banks.sandbox + g.banks.production}</td>
                      <td className="num">{g.player_accounts.sandbox} / {g.player_accounts.production}</td>
                      <td className="num">{g.active_api_keys.sandbox} / {g.active_api_keys.production}</td>
                      <td>{formatDateTime(g.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager total={list.data.total} limit={limit} offset={offset} onChange={setOffset} />
          </>
        )}
      </Panel>
    </Shell>
  );
}

export function AdminGameDetail({ id }: { id: string }) {
  const api = adminApi(useClient());
  const toast = useToast();
  const q = useQuery(`admin:game:${id}`, () => api.game(id));
  const audit = useQuery(`admin:audit:game:${id}`, () => api.auditLogs({ game_id: id, limit: 20 }));
  const [statusOpen, setStatusOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [revokeReason, setRevokeReason] = useState("");
  const g = q.data;
  const setStatus = useAction((s: string) => api.setGameStatus(id, s, reason.trim() || undefined));
  const revoke = useAction(async (k: ApiKey) => {
    await api.revokeKey(k.id, revokeReason.trim() || undefined);
    return true;
  });

  return (
    <Shell crumbs={[{ label: "Games", to: "/admin/games" }, { label: g?.name ?? "Game" }]}>
      <ErrorNotice error={q.error} onRetry={q.reload} />
      {!g && q.loading && <Loading />}
      {g && (
        <>
          <PageHead
            title={g.name}
            badge={<GameStatusBadge status={g.status} />}
            lede={<>By <Link to={`/admin/developers/${g.developer_id}`}>{g.developer_name}</Link>, created {formatDateTime(g.created_at)}</>}
            actions={
              g.status === "closed" ? undefined : (
                <Button variant={g.status === "active" ? "danger" : "primary"} onClick={() => setStatusOpen(true)}>
                  {g.status === "active" ? "Suspend game" : "Reactivate game"}
                </Button>
              )
            }
          />
          {g.status === "suspended" && (
            <div className="notice warn" style={{ marginBottom: 20 }}>Suspended. Every API key for this game is refused until you reactivate it.</div>
          )}
          {g.status === "closed" && (
            <div className="notice warn" style={{ marginBottom: 20 }}>
              Deleted by its developer{g.updated_at ? ` on ${formatDateTime(g.updated_at)}` : ""}. Its keys are revoked and it can't be reopened. The ledger and audit history below are kept.
            </div>
          )}
          <div className="stack">
            <Panel title="Banks" sub="Issued is the money each bank's issuance account has put into circulation." flush>
              {g.bank_list.length === 0 ? (
                <Empty title="No banks yet" />
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Bank</th>
                        <th>Environment</th>
                        <th>Code</th>
                        <th className="num">Player accounts</th>
                        <th className="num">Issued</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.bank_list.map((b) => (
                        <tr key={b.id}>
                          <td>
                            <div className="cell-title">{b.name}</div>
                            <div className="cell-sub">{b.currency}</div>
                          </td>
                          <td><EnvBadge env={b.environment} /></td>
                          <td className="mono">{b.bank_code}</td>
                          <td className="num">{b.player_accounts}</td>
                          <td className="num"><Money minor={b.issued_minor} currency={{ code: b.currency, exponent: b.exponent, symbol: b.symbol }} /></td>
                          <td><StatusBadge status={b.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <Panel title="API keys" sub="Revoking here is an emergency control: the key stops working on its next request." flush>
              {g.api_keys.length === 0 ? (
                <Empty title="No keys issued" />
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Key</th>
                        <th>Environment</th>
                        <th>Scopes</th>
                        <th>Last used</th>
                        <th>Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {g.api_keys.map((k) => (
                        <tr key={k.id}>
                          <td>
                            <div className="cell-title">{k.label}</div>
                            <div className="cell-sub mono">{k.prefix}</div>
                          </td>
                          <td><EnvBadge env={k.environment} /></td>
                          <td>
                            <div className="scope-list">
                              {k.scopes.map((s) => <code key={s} className={POWER_SCOPES.has(s) ? "power" : ""}>{s}</code>)}
                            </div>
                          </td>
                          <td>{relativeTime(k.last_used_at)}</td>
                          <td><StatusBadge status={k.revoked_at ? "revoked" : "active"} /></td>
                          <td className="num">{!k.revoked_at && <Button size="small" variant="danger" onClick={() => setRevoking(k)}>Revoke</Button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <Panel title="Game clocks">
              <dl className="facts">
                {g.clocks.map((c) => (
                  <div key={c.environment} style={{ display: "contents" }}>
                    <dt><EnvBadge env={c.environment ?? ""} /></dt>
                    <dd>
                      {formatGameTime(c.game_time)} UTC, {c.mode.replace("_", "-")}
                      {c.mode === "scaled" ? ` at ${c.speed}×` : ""}, days roll over in {c.time_zone}
                    </dd>
                  </div>
                ))}
              </dl>
            </Panel>

            <Panel title="Recent activity" actions={<Link to={`/admin/audit?game_id=${id}`}>See all</Link>} flush>
              <ErrorNotice error={audit.error} />
              {audit.data && <AuditTable rows={audit.data.data} showTenant={false} />}
            </Panel>
          </div>

          <Confirm
            open={statusOpen}
            title={g.status === "active" ? `Suspend ${g.name}?` : `Reactivate ${g.name}?`}
            body={g.status === "active"
              ? "All of this game's API keys are refused from the next request, in sandbox and production. Balances and history stay intact."
              : "Its unrevoked keys work again immediately."}
            confirmLabel={g.status === "active" ? "Suspend game" : "Reactivate game"}
            danger={g.status === "active"}
            busy={setStatus.pending}
            error={setStatus.error}
            onConfirm={async () => {
              if (await setStatus.run(g.status === "active" ? "suspended" : "active")) {
                setStatusOpen(false);
                setReason("");
                invalidate("admin:");
                toast(g.status === "active" ? "Game suspended" : "Game reactivated");
              }
            }}
            onClose={() => setStatusOpen(false)}
          >
            <Field label="Reason (recorded in the audit log)">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. abuse report #142" />
            </Field>
          </Confirm>
          <Confirm
            open={!!revoking}
            title="Revoke this API key?"
            body={<>The <EnvBadge env={revoking?.environment ?? ""} /> key <span className="mono">{revoking?.prefix}</span> stops working immediately. The developer will need to issue a new one.</>}
            confirmLabel="Revoke key"
            danger
            busy={revoke.pending}
            error={revoke.error}
            onConfirm={async () => {
              if (revoking && (await revoke.run(revoking))) {
                setRevoking(null);
                setRevokeReason("");
                invalidate("admin:");
                toast("Key revoked");
              }
            }}
            onClose={() => setRevoking(null)}
          >
            <Field label="Reason (recorded in the audit log)">
              <Input value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} placeholder="e.g. key leaked in a public repo" />
            </Field>
          </Confirm>
        </>
      )}
    </Shell>
  );
}
